// The same assembled order as the office preview, seen from the phone: what
// our driver — or the subcontractor — is asked to do in myDriver, stop by
// stop. A picture of the app, not the app: the controls are drawn to show
// what each kind of checkpoint asks for, and do nothing.
//
// Planner-only items are not shown; a planner + on-site item shows its on-site
// half. Stops done by the other party collapse to one line so the journey
// still reads in order. Required items gate the stop's Depart button — the one
// place anything is mandatory — while a failed answer only ever warns.
import type { TripCheckpointAssembledItem, TripCheckpointPreview } from '@my/tms-api';
import { cn } from '@my/ui';
import { Camera, Minus, PenLine, Plus, ScanLine, Thermometer } from 'lucide-react';
import type { ReactNode } from 'react';
import { taskTypeLabel } from '@/features/orders/lib/taskTypeConfig';
import { MOMENT_LABEL, MOMENT_ORDER, type Checkpoint, type OnSiteRole } from '../lib/model';
import { checkpointFromWire, roleFromWire } from '../lib/wire';
import type { PreviewNames } from './OrderPreview';

// myDriver's own palette (My.Stratum src/constants/theme.ts), so the picture
// reads as the app.
const NAVY = '#002E72';
const BRAND_MY = '#FFC72C';

const asks = (item: TripCheckpointAssembledItem, role: OnSiteRole) =>
  item.skipped === null && item.askedOf.some((r) => roleFromWire(r) === role);

export function PhonePreview({
  preview,
  role,
  orderLabel,
  names,
  stopIds,
  recipientName,
  message,
}: {
  preview: TripCheckpointPreview;
  role: OnSiteRole;
  orderLabel: string;
  names: PreviewNames;
  /**
   * Exactly these stops — a send to one driver. Without it, every stop the role
   * could do (the Settings preview, where two drivers are both "Driver").
   */
  stopIds?: ReadonlySet<string>;
  /** Shown in the app bar instead of "Our driver". */
  recipientName?: string;
  /** The planner's note on the job card. */
  message?: string | null;
}) {
  const place = (addressId: string | null) => (addressId ? names.places.get(addressId) : undefined);
  const before = preview.order.filter((i) => asks(i, role));
  const isMine = (t: TripCheckpointPreview['tasks'][number]) =>
    stopIds ? stopIds.has(t.orderTaskId) : t.doneBy === null || roleFromWire(t.doneBy) === role;
  const mine = preview.tasks.filter(isMine);
  /** Who does a stop that isn't in this send, as the driver should read it. */
  const theirs = (t: TripCheckpointPreview['tasks'][number]) =>
    t.doneBy === 'Sub' ? 'a subcontractor' : t.doneBy === null ? 'not assigned yet' : role === 'driver' ? 'another driver' : 'our driver';
  const orderNumber = preview.facts.orderNumber || (orderLabel.split(' · ')[0] ?? orderLabel);

  return (
    <div className="flex justify-center px-3 py-4">
      <div className="w-[300px] overflow-hidden rounded-[2.2rem] border-[6px] border-slate-800 bg-[#F7F7F2] shadow-lg">
        {/* App bar */}
        <div className="flex items-center justify-between px-4 pb-2.5 pt-3" style={{ backgroundColor: NAVY }}>
          <span className="text-sm font-semibold text-white">
            <span style={{ color: BRAND_MY }}>my</span>Tms
          </span>
          <span className="text-[10px] text-white/70">
            {recipientName ?? (role === 'driver' ? 'Our driver' : 'Subcontractor')}
          </span>
        </div>

        <div className="max-h-[640px] space-y-3 overflow-y-auto p-3">
          {/* Job card */}
          <section className="rounded-2xl bg-white p-3 shadow-sm">
            <p className="text-[10px] text-slate-400">{orderNumber}</p>
            <h4 className="text-base font-semibold text-slate-900">
              {names.customer ?? preview.facts.cargoDefinitionName}
            </h4>
            <p className="text-[11px] text-slate-500">
              {names.customer && `${preview.facts.cargoDefinitionName} · `}
              {mine.length} stop{mine.length === 1 ? '' : 's'} for you
              {mine.length < preview.tasks.length && ` · ${preview.tasks.length - mine.length} by others`}
            </p>
            {message?.trim() && (
              <p className="mt-2 border-l-2 border-emerald-600 pl-2 text-[11px] text-slate-700">{message.trim()}</p>
            )}

            {before.length > 0 && (
              <div className="mt-2.5 border-t border-slate-100 pt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  Before you start
                </p>
                <Steps items={before} />
              </div>
            )}

            <div className="mt-3 flex gap-2">
              <span
                className="flex h-9 flex-1 items-center justify-center rounded-lg text-sm font-semibold text-white"
                style={{ backgroundColor: NAVY }}
              >
                Accept
              </span>
              <span
                className="flex h-9 items-center justify-center rounded-lg border px-4 text-sm font-semibold"
                style={{ borderColor: NAVY, color: NAVY }}
              >
                Query
              </span>
            </div>
            {before.some((i) => i.checkpoint.required) && (
              <p className="mt-1.5 text-center text-[10px] text-slate-400">
                Required items must be done before the job starts.
              </p>
            )}
          </section>

          {/* Stops, in order */}
          {preview.tasks.map((task) => {
            const index = mine.indexOf(task);
            if (!isMine(task)) {
              return (
                <p
                  key={task.orderTaskId}
                  className="rounded-xl border border-dashed border-slate-300 px-3 py-2 text-[11px] text-slate-400"
                >
                  {taskTypeLabel(task.taskType)} · {place(task.addressId) ?? `stop ${task.sequence}`} —{' '}
                  {theirs(task) === 'not assigned yet' ? theirs(task) : `done by ${theirs(task)}`}
                </p>
              );
            }
            const items = task.items.filter((i) => asks(i, role));
            const required = items.filter((i) => i.checkpoint.required).length;
            return (
              <section key={task.orderTaskId} className="rounded-2xl bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Stop {index + 1} of {mine.length}
                  </p>
                  {task.doneBy === null && <span className="text-[10px] text-amber-700">Not assigned yet</span>}
                </div>
                <h4 className="text-sm font-semibold text-slate-900">
                  {place(task.addressId) ?? taskTypeLabel(task.taskType)}
                </h4>
                {place(task.addressId) && (
                  <p className="text-[11px] text-slate-500">{taskTypeLabel(task.taskType)}</p>
                )}
                <span
                  className="mt-2 flex h-8 items-center justify-center rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: '#DEE9F7', color: '#00224B' }}
                >
                  Navigate{place(task.addressId) ? ` to ${place(task.addressId)!.split(',')[0]}` : ''}
                </span>

                <p className="mt-2.5 text-[10px] text-emerald-700">● Arrival recorded by the app</p>
                {items.length === 0 ? (
                  <p className="py-2 text-[11px] text-slate-400">Nothing to check here.</p>
                ) : (
                  MOMENT_ORDER.map((moment) => {
                    const group = items.filter((i) => checkpointFromWire(i.checkpoint).moment === moment);
                    if (group.length === 0) return null;
                    return (
                      <div key={moment} className="mt-2">
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          {MOMENT_LABEL[moment]}
                        </p>
                        <Steps items={group} />
                      </div>
                    );
                  })
                )}

                <span className="mt-3 flex h-9 items-center justify-center rounded-lg bg-slate-200 text-xs font-semibold text-slate-500">
                  {required > 0 ? `Complete ${required} required to depart` : 'Depart'}
                </span>
              </section>
            );
          })}

          {mine.length === 0 && (
            <p className="px-2 py-4 text-center text-xs text-slate-500">
              {role === 'driver'
                ? 'Every stop on this order is subcontracted — our driver gets nothing on it.'
                : 'Our drivers do every stop — no subcontractor gets this job.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Steps({ items }: { items: TripCheckpointAssembledItem[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const c = checkpointFromWire(item.checkpoint);
        return (
          <li key={item.key} className="flex gap-2">
            <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 border-slate-300" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-900">
                {c.label || 'Untitled checkpoint'}
                {!c.required && <span className="text-slate-400"> · optional</span>}
              </p>
              {c.hint && <p className="text-[10px] text-slate-500">{c.hint}</p>}
              <Control checkpoint={c} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const box = 'mt-1 flex h-7 items-center rounded-md border border-slate-200 bg-slate-50 px-2 text-[11px] text-slate-500';

/** A drawing of what the step asks for. */
function Control({ checkpoint: c }: { checkpoint: Checkpoint }) {
  const cfg = c.config;
  switch (c.kind) {
    case 'temperature':
      return (
        <Row>
          <span className={cn(box, 'w-20 gap-1')}>
            <Thermometer className="h-3 w-3" /> °C
          </span>
          <Note>
            {cfg.tempRange === 'cargo'
              ? `Set temperature ± ${cfg.toleranceC} °C`
              : `Allowed ${cfg.minC} to ${cfg.maxC} °C`}
          </Note>
        </Row>
      );
    case 'quantity':
      return (
        <Row>
          <span className="mt-1 flex h-7 items-center overflow-hidden rounded-md border border-slate-200 bg-slate-50 text-[11px] text-slate-600">
            <Minus className="mx-1.5 h-3 w-3" />
            <span className="border-x border-slate-200 px-2">0</span>
            <Plus className="mx-1.5 h-3 w-3" />
          </span>
          <Note>
            {cfg.unit}
            {cfg.compareToPlanned && ' · checked against planned'}
          </Note>
        </Row>
      );
    case 'identifier':
      return (
        <Row>
          <span className={cn(box, 'gap-1')}>
            <ScanLine className="h-3 w-3" /> {cfg.compareToPlanned ? 'Confirm the number' : 'Scan or type'}
          </span>
          <Note>{cfg.identifierOf === 'bic' ? 'Container / tank' : cfg.identifierOf === 'seal' ? 'Seal' : 'Reference'}</Note>
        </Row>
      );
    case 'photo':
      return (
        <span className={cn(box, 'w-fit gap-1')}>
          <Camera className="h-3 w-3" /> Take photo{cfg.minPhotos > 1 ? `s (${cfg.minPhotos})` : ''}
        </span>
      );
    case 'signature':
      return (
        <span className="mt-1 flex h-12 items-end gap-1 rounded-md border border-dashed border-slate-300 bg-slate-50 px-2 pb-1 text-[10px] text-slate-400">
          <PenLine className="h-3 w-3" /> Sign here · print name
        </span>
      );
    case 'yesNo':
      return <Pair left="Yes" right="No" />;
    case 'inspection':
      return <Pair left="Pass" right="Fail" note="A fail asks for a photo" />;
    case 'reading':
      return <span className={cn(box, 'w-28')}>{cfg.unit}</span>;
    case 'note':
      return <span className={cn(box, 'w-full')}>Type a note…</span>;
    case 'document':
      return <span className={cn(box, 'w-fit gap-1')}>Attach or confirm</span>;
    default:
      return null;
  }
}

function Row({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-x-2">{children}</div>;
}

function Note({ children }: { children: ReactNode }) {
  return <span className="mt-1 text-[10px] text-slate-400">{children}</span>;
}

function Pair({ left, right, note }: { left: string; right: string; note?: string }) {
  return (
    <Row>
      <span className="mt-1 flex gap-1">
        <span className="rounded-md border border-slate-200 px-3 py-1 text-[11px] text-slate-600">{left}</span>
        <span className="rounded-md border border-slate-200 px-3 py-1 text-[11px] text-slate-600">{right}</span>
      </span>
      {note && <Note>{note}</Note>}
    </Row>
  );
}
